import type { FAQItem } from "@/lib/products";

/**
 * FAQ data for the Video Bitrate Calculator tool
 * Separated from view layer for maintainability
 */
export const videoBitrateCalculatorFAQ: FAQItem[] = [
  {
    question: "How does the Video Bitrate Calculator work?",
    answer:
      "The tool calculates optimal video bitrates or estimated file sizes based on your video duration, audio bitrate, and either a target file size or video bitrate. It uses the formula: File Size (MB) = (Total Bitrate in kbps × Duration in seconds) / 8192. You can work in two modes: calculate the bitrate needed to fit a target file size, or calculate the file size from a given bitrate.",
  },
  {
    question: "What is video bitrate?",
    answer:
      "Bitrate is the amount of data processed per second in your video file, measured in kilobits per second (kbps). Higher bitrates mean better quality but larger file sizes. Lower bitrates reduce file size but may reduce quality. The calculator helps you find the sweet spot between quality and file size for your specific needs.",
  },
  {
    question: "What bitrate should I use for different resolutions?",
    answer:
      "Typical bitrates vary by resolution: 720p HD usually needs 2500-4000 kbps, 1080p Full HD needs 4000-8000 kbps, and 4K needs 15000-25000+ kbps. However, the optimal bitrate also depends on your content (fast motion needs higher bitrates), codec, and target file size. The calculator helps you find the right balance.",
  },
  {
    question: "What audio bitrate should I use?",
    answer:
      "Common audio bitrates: 64 kbps for speech-only content, 128 kbps for standard quality, 192 kbps for high quality, and 256-320 kbps for studio/master quality. For most videos, 128-192 kbps provides a good balance. The calculator accounts for audio bitrate when calculating total file size, so higher audio bitrates reduce the available video bitrate for a given file size limit.",
  },
  {
    question: "How do I stay under Discord's file size limits?",
    answer:
      "Discord has different limits: 8MB for free users, 25MB for Nitro Classic, and 500MB for Nitro. Use the 'Calculate Bitrate (Fit to Size)' mode, enter your video duration, set the target size to your Discord limit, and the tool will tell you the maximum video bitrate you can use. The quick buttons (8MB, 25MB, etc.) make this even faster.",
  },
  {
    question: "Can I use this for email attachments?",
    answer:
      "Yes! Most email providers limit attachments to 25MB. Use the calculator in 'Fit to Size' mode with a 25MB target to find the optimal bitrate for your video duration. This ensures your video will be small enough to send via email while maintaining the best possible quality within that constraint.",
  },
  {
    question: "How accurate are the file size estimates?",
    answer:
      "The estimates are very accurate for most video codecs (H.264, H.265/HEVC) and standard encoding settings. However, actual file sizes can vary slightly based on your encoder settings, codec efficiency, video content complexity, and container format. The calculator provides a reliable starting point that you can fine-tune during encoding.",
  },
  {
    question: "What if my calculated bitrate is too low?",
    answer:
      "If the calculated bitrate is very low (under 1000 kbps), you may need to either reduce your video duration, increase your target file size limit, or accept lower quality. Very low bitrates can cause visible compression artifacts. Consider splitting long videos into shorter segments or using a more efficient codec like H.265/HEVC if your target platform supports it.",
  },
  {
    question: "Is this tool free?",
    answer:
      "Yes, completely free with no registration required. You can calculate bitrates and file sizes as many times as you need. There are no limits, no watermarks, and no hidden fees. It's part of our free tools collection to help content creators, streamers, and anyone who needs to optimize video file sizes for different platforms.",
  },
];
